import { useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'

// local imports - types
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import { IFilteredLactationRoomList } from 'types/LactationType'
import useGetLactationRoomResTable from '../hooks/use-get-lactation-room-res-table'
import Scheduler from './Scheduler'
import ScheduleSidebar from './ScheduleSidebar'
import ReservationModalHeader from './ReservationModalHeader'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import { ActionArea, CancelButton, ConfirmButton } from '../../gym/gym-list/styled'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  filterDate: Date
  selectedRoom: IFilteredLactationRoomList
  open: boolean
  closeModal: (resetRes?: boolean) => void
  openModal: () => void
  showNotification: () => void
}

export default function ReservationModalWrapper({
  filterDate: dateFromList,
  selectedRoom,
  open,
  closeModal,
  openModal,
  showNotification,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Lactation Room - reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  useEffect(() => setFilterDate(dateFromList), [dateFromList])

  /* GET Selected Lactation Room's Reservation Table List | Custom Hook  */
  const salonLocationId = selectedRoom.floor.locationId
  const lactationRoomId = selectedRoom.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservation,
    setSelectedReservation,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetLactationRoomResTable(salonLocationId, lactationRoomId, dateFromList)

  /* Open Appointment Sidebar */
  const openAppointmentSidebar = () => {
    setSidebarVisible(true)
    closeModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    setSidebarVisible(false)
    setSelectedReservation(undefined)
    showNotification()
    setFilterDate(dateFromList)
    setTableWeek(lastFilteredDate)
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    closeModal(true)
    setFilterDate(dateFromList)
    setSelectedReservation(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        focusOnShow={false}
        closeOnEscape={false}
        closable={true}
        header={<span>{`${t('lactation-res-modal.lactation-reservation')}`}</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedReservation}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          room={selectedRoom}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedReservation}
            setSelectedAppointment={setSelectedReservation}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservation && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          roomInfo={selectedRoom}
          reservationInfo={selectedReservation}
        />
      )}
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}
